import React, { useState } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';
import { brand } from '../../design-system/theme/tokens';

interface EditableTagListProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  color?: string;
}

export default function EditableTagList({ tags, onChange, placeholder, color = brand.primary }: EditableTagListProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
      setInputValue('');
    }
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  return (
    <View>
      {/* Tags display */}
      {tags.length > 0 && (
        <View style={styles.tagsContainer}>
          {tags.map((tag, index) => (
            <View key={index} style={[styles.tag, { backgroundColor: color + '15', borderColor: color + '40' }]}>
              <Text style={[styles.tagText, { color }]} numberOfLines={1}>{tag}</Text>
              <TouchableOpacity onPress={() => removeTag(index)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <MaterialIcons name="close" size={14} color={color} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
      {/* Input row */}
      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          value={inputValue}
          onChangeText={setInputValue}
          placeholder={placeholder || t('schoolManagement.frameworksSection.tagPlaceholder')}
          placeholderTextColor="#9CA3AF"
          onSubmitEditing={addTag}
          returnKeyType="done"
        />
        <TouchableOpacity
          style={[styles.addButton, { backgroundColor: color }]}
          onPress={addTag}
          disabled={!inputValue.trim()}
        >
          <Text style={styles.addButtonText}>{t('schoolManagement.frameworksSection.addTag')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    borderWidth: 1,
    gap: 4,
    maxWidth: '100%',
  },
  tagText: {
    fontSize: 13,
    flexShrink: 1,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 8,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: '#111827',
    backgroundColor: '#FFFFFF',
  },
  addButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
});
